/** Formatting helpers shared across the website. All money is formatted from the
 *  currency the API sends with each amount — never a hardcoded symbol. */

const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function formatMoney(amount: number, currency = 'EUR', locale = 'en-GB'): string {
  const key = `${locale}:${currency}`;
  let formatter = currencyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      // Show cents only when the price actually has them (€16 not €16.00, but €3.50).
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
    // Cache keyed by currency only when the fraction rule does not vary.
    if (!Number.isInteger(amount)) currencyFormatters.set(key, formatter);
  }
  return formatter.format(amount);
}

/** Money with explicit cents, for totals and invoices. */
export function formatMoneyExact(amount: number, currency = 'EUR', locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** "Friday 18 September 2026" from an ISO date (yyyy-MM-dd). */
export function formatLongDate(isoDate: string, locale = 'en-GB'): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Fri 18 Sep" — compact form for cards and lists. */
export function formatShortDate(isoDate: string, locale = 'en-GB'): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Renders a UTC instant in the venue's time zone, so an event that starts at 21:00 in
 * Milano reads as 21:00 regardless of where the visitor is.
 */
export function formatInVenueTime(
  utcIso: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
  locale = 'en-GB',
): string {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(new Date(utcIso));
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(new Date(utcIso));
  }
}

/** Today's date in the venue time zone as yyyy-MM-dd, for date input defaults. */
export function todayInZone(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function addDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
